import { api } from "./api";
import { base64UrlToBytes, pushState, type PushState } from "./pushSupport";

const WORKER = "/sw.js";

function isIOS(): boolean {
	// iPadOS reports itself as a Mac; the touch points give it away.
	return /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

function isStandalone(): boolean {
	return matchMedia("(display-mode: standalone)").matches || (navigator as { standalone?: boolean }).standalone === true;
}

async function currentSubscription(): Promise<PushSubscription | null> {
	if (!("serviceWorker" in navigator)) return null;
	const registration = await navigator.serviceWorker.getRegistration(WORKER);
	return (await registration?.pushManager?.getSubscription()) ?? null;
}

export async function currentPushState(): Promise<PushState> {
	const serviceWorker = "serviceWorker" in navigator;
	const pushManager = "PushManager" in window;
	return pushState({
		serviceWorker,
		pushManager,
		iOS: isIOS(),
		standalone: isStandalone(),
		permission: "Notification" in window ? Notification.permission : "default",
		subscribed: serviceWorker && pushManager ? Boolean(await currentSubscription().catch(() => null)) : false,
	});
}

/** Asks for permission and subscribes this device. Call it straight from a click: iOS only prompts for a user gesture. */
export async function enablePush(): Promise<PushState> {
	// Before any other await, while the click still counts as the gesture.
	const permission = await Notification.requestPermission();
	if (permission !== "granted") return currentPushState();
	const registration = await navigator.serviceWorker.register(WORKER);
	await navigator.serviceWorker.ready;
	const { publicKey } = await api.pushKey();
	const subscription =
		(await registration.pushManager.getSubscription()) ??
		(await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: base64UrlToBytes(publicKey) }));
	await api.subscribePush(subscription.toJSON());
	return "on";
}

/** Unsubscribes this device, on the server first while the login is still valid. */
export async function disablePush(): Promise<void> {
	const subscription = await currentSubscription().catch(() => null);
	if (!subscription) return;
	await api.unsubscribePush(subscription.endpoint).catch(() => {});
	await subscription.unsubscribe().catch(() => false);
}
